import React, { useRef, useState } from 'react';
import { Card, Button, Badge } from '@medikei/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  filesApi,
  httpClient,
  type DocumentType,
  type DoctorDocument,
} from '@/api';
import {
  useDoctorDocuments,
  useUploadDoctorDocument,
  useDeleteDoctorDocument,
} from '@/hooks/doctor/useDoctorDocuments';
import {
  Upload,
  Trash2,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Shield,
  Loader2,
} from 'lucide-react';

export interface DoctorDocumentsSectionProps {
  doctorId: string;
  className?: string;
}

interface CredentialDocConfig {
  type: DocumentType;
  title: string;
  description: string;
  required: boolean;
  requiredNote?: string;
}

const CREDENTIAL_DOCS: CredentialDocConfig[] = [
  {
    type: 'CRM',
    title: 'Carteira do CRM (Frente e Verso)',
    description: 'Documento oficial de identidade profissional emitido pelo CRM ativo e com foto legível.',
    required: true,
  },
  {
    type: 'DIPLOMA',
    title: 'Diploma de Graduação em Medicina',
    description: 'Frente e verso do diploma autenticado de conclusão da graduação em Medicina (MEC).',
    required: true,
  },
  {
    type: 'IDENTITY',
    title: 'Documento com Foto (RG / CNH)',
    description: 'Documento oficial com foto válido em todo território nacional (RG ou CNH).',
    required: true,
  },
  {
    type: 'RQE',
    title: 'RQE / Registro de Especialista',
    description: 'Comprovante do Registro de Qualificação de Especialista emitido pelo CRM/AMB.',
    required: false,
    requiredNote: 'Obrigatório se especialista',
  },
];

export function DoctorDocumentsSection({ doctorId, className = '' }: DoctorDocumentsSectionProps) {
  const { data: documents, isLoading, refetch } = useDoctorDocuments();
  const uploadMutation = useUploadDoctorDocument();
  const deleteMutation = useDeleteDoctorDocument();
  const { toast } = useToast();

  const [uploadingType, setUploadingType] = useState<DocumentType | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRefs = useRef<{ [key in DocumentType]?: HTMLInputElement | null }>({});

  const handleFileChange = async (type: DocumentType, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!doctorId) {
      toast({
        title: 'Identificação indisponível',
        description: 'Não foi possível identificar o médico para upload.',
        variant: 'error',
      });
      return;
    }

    const MAX_SIZE = 15 * 1024 * 1024; // 15MB
    if (file.size > MAX_SIZE) {
      toast({
        title: 'Arquivo muito grande',
        description: 'O tamanho do arquivo deve ser de no máximo 15MB.',
        variant: 'error',
      });
      return;
    }

    setUploadingType(type);
    try {
      // 1. Upload file to files storage
      const uploadedFile = await filesApi.uploadFile(
        file,
        doctorId,
        'DOCTOR',
        'DOCUMENT',
      );

      // 2. Register document link in doctor documents
      await uploadMutation.mutateAsync({
        type,
        fileUrl: uploadedFile.key,
      });

      toast({
        title: 'Documento enviado!',
        description: 'Seu documento foi enviado com sucesso e está em análise.',
      });

      await refetch();
    } catch (err) {
      console.error('Falha no upload do documento:', err);
      const msg = err instanceof Error ? err.message : 'Não foi possível enviar o documento.';
      toast({
        title: 'Erro no envio',
        description: msg,
        variant: 'error',
      });
    } finally {
      setUploadingType(null);
      if (fileInputRefs.current[type]) {
        fileInputRefs.current[type]!.value = '';
      }
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Deseja realmente excluir este documento?')) return;

    setDeletingId(docId);
    try {
      await deleteMutation.mutateAsync(docId);
      toast({
        title: 'Documento excluído',
        description: 'O documento foi removido com sucesso.',
      });
      await refetch();
    } catch (err) {
      console.error('Falha ao excluir documento:', err);
      const msg = err instanceof Error ? err.message : 'Não foi possível excluir o documento.';
      toast({
        title: 'Erro ao excluir',
        description: msg,
        variant: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewFile = async (doc: DoctorDocument) => {
    if (!doc.fileUrl) {
      toast({
        title: 'Arquivo indisponível',
        description: 'URL do documento não encontrada.',
        variant: 'error',
      });
      return;
    }

    if (
      doc.fileUrl.startsWith('http://') ||
      doc.fileUrl.startsWith('https://') ||
      doc.fileUrl.startsWith('blob:')
    ) {
      window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const signed = await filesApi.getSignedUrl(doc.fileUrl, 3600);
      if (signed?.url) {
        window.open(signed.url, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch (e) {
      console.warn('Não foi possível obter URL assinada direta:', e);
    }

    const apiBase = httpClient['baseUrl'] || '';
    window.open(`${apiBase}/files/${encodeURIComponent(doc.fileUrl)}/download`, '_blank', 'noopener,noreferrer');
  };

  const renderStatusBadge = (doc?: DoctorDocument) => {
    if (!doc) {
      return (
        <Badge variant="outline" className="bg-muted text-muted-foreground border-border flex items-center gap-1 text-xs">
          <Clock className="h-3 w-3" />
          Pendente de envio
        </Badge>
      );
    }

    switch (doc.status) {
      case 'APPROVED':
        return (
          <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 flex items-center gap-1 text-xs">
            <CheckCircle className="h-3 w-3" />
            Aprovado pelo CRM/Admin
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30 flex items-center gap-1 text-xs">
            <Clock className="h-3 w-3" />
            Em análise
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30 flex items-center gap-1 text-xs">
            <XCircle className="h-3 w-3" />
            Recusado
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-xs">
            {doc.status}
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <Card className={`p-6 bg-card border-border ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card id="documentos" className={`p-6 bg-card border-border scroll-mt-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 mb-6 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-primary-500/10 text-primary-500">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-foreground">Documentos Comprobatórios</h3>
            <p className="text-sm text-muted-foreground">
              Documentação necessária para validação do seu cadastro médico pelo CRM e plataforma
            </p>
          </div>
        </div>
        <Badge variant="outline" className="self-start sm:self-auto bg-primary-500/5 text-primary-600 border-primary-500/20">
          Credenciamento Médico
        </Badge>
      </div>

      {/* Document List */}
      <div className="space-y-4">
        {CREDENTIAL_DOCS.map((docConfig) => {
          const doc = documents?.find((d) => d.type === docConfig.type);
          const isUploading = uploadingType === docConfig.type;
          const isDeleting = doc ? deletingId === doc.id : false;
          const canModify = !doc || doc.status === 'PENDING' || doc.status === 'REJECTED';

          return (
            <div
              key={docConfig.type}
              className="p-4 rounded-lg border border-border bg-card/50 hover:bg-muted/30 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                {/* Info */}
                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground text-sm sm:text-base">
                      {docConfig.title}
                    </span>
                    {docConfig.required ? (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                        Obrigatório
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {docConfig.requiredNote || 'Opcional'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {docConfig.description}
                  </p>
                </div>

                {/* Status Badge */}
                <div className="shrink-0 self-start">
                  {renderStatusBadge(doc)}
                </div>
              </div>

              {/* Rejection Alert */}
              {doc?.status === 'REJECTED' && doc.rejectionReason && (
                <div className="mt-3 p-3 rounded-md bg-red-500/10 border border-red-500/20 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Justificativa da recusa: </span>
                    <span>{doc.rejectionReason}</span>
                  </div>
                </div>
              )}

              {/* Actions Footer */}
              <div className="mt-4 pt-3 border-t border-border/50 flex flex-wrap items-center justify-between gap-3">
                <div>
                  {doc && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewFile(doc)}
                      className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1.5 px-2.5 h-8"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Visualizar Arquivo
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    ref={(el) => {
                      fileInputRefs.current[docConfig.type] = el;
                    }}
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => handleFileChange(docConfig.type, e)}
                  />

                  {/* Substitute / Reupload Button */}
                  {canModify && doc && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isUploading || isDeleting}
                        onClick={() => fileInputRefs.current[docConfig.type]?.click()}
                        className="text-xs flex items-center gap-1.5 h-8"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3.5 w-3.5" />
                            Substituir
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isUploading || isDeleting}
                        onClick={() => handleDelete(doc.id)}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-500/10 h-8 px-2"
                        title="Excluir documento"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </>
                  )}

                  {/* Initial Upload Button */}
                  {!doc && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isUploading}
                      onClick={() => fileInputRefs.current[docConfig.type]?.click()}
                      className="text-xs flex items-center gap-1.5 h-8 bg-primary-500/5 hover:bg-primary-500/10 text-primary-600 border-primary-500/30"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Upload className="h-3.5 w-3.5" />
                          Enviar Documento
                        </>
                      )}
                    </Button>
                  )}

                  {/* Approved status notice */}
                  {doc?.status === 'APPROVED' && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      Documento validado
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
